import axios from "axios";

import PermissionM from "react-lib/apis/issara/users/PermissionM";
import MedicalDescriptionList from "issara-sdk/apis/MedicalDescriptionList_apps_DEN";
import DentalIcd10List from "issara-sdk/apis/DentalIcd10List_apps_DEN";
import DentalClinicalFindingList from "issara-sdk/apis/DentalClinicalFindingList_apps_DEN";
import ClinicalTermList from "issara-sdk/apis/ClinicalTermList_core";
import ChoiceView from "issara-sdk/apis/ChoiceView_core";
import ClinicalTermSetView from "issara-sdk/apis/ClinicalTermSetView_core";
import TreatmentList from "issara-sdk/apis/TreatmentList_apps_TRT";
import DivisionHasUserList from "issara-sdk/apis/DivisionHasUserList_apps_PRX";

// Master data และข้อมูลของ user โหลดครั้งเดียวต่อ user แล้ว cache ไว้

export type DENUserPermission = {
  rolegroup_CAN_VIEW_SECRET_DOCUMENT: boolean;
  role_PEDIATRIC_DENTISTS: boolean;
};

export type DENMaster = {
  data: {
    organ?: { name: string; items: any[] };
    clinicaltags?: { name: string; items: any[] };
    icd10?: { name: string; items: any[] };
    condition?: { name: string; items: any[] };
    academicdx?: { name: string; items: any[] };
  };
  permission: DENUserPermission;
  // options ของ dropdown
  options: {
    urgencyOptions: DENOption[];
    statusExcludeCompleteOptions: DENOption[];
    statusIncludeCompleteOptions: DENOption[];
    anestheticOptions: DENOption[];
    treatmentOptions: DENOption[];
    priceChangeReasonOptions: DENOption[];
    // แผนกที่ user มีสิทธิ์ พร้อม useRights
    divisionHasUserOptions: (DENOption & { useRights: string[] })[];
  };
};

export type DENOption = { key: number; text: string; value: any; text_en?: string };

type LoadParams = {
  userId?: number | string | null;
  masterData?: Record<string, any>;
};

let cacheKey: string | null = null;
let cache: Promise<DENMaster> | null = null;

export const loadDENMaster = (controller: any, params: LoadParams): Promise<DENMaster> => {
  const key = `${params.userId ?? ""}`;

  if (!cache || cacheKey !== key) {
    cacheKey = key;
    cache = fetchDENMaster(controller, params).catch((error) => {
      cache = null;
      throw error;
    });
  }

  return cache;
};

export const clearDENMasterCache = () => {
  cacheKey = null;
  cache = null;
};

const fetchDENMaster = async (controller: any, params: LoadParams): Promise<DENMaster> => {
  const host = params.masterData || {};

  // ใช้ของ host ก่อนถ้ามี
  const [organ, clinicaltags, icd10, condition, academicdx, permission, options] = await Promise.all([
    host.organ?.items?.length ? host.organ : GetOrgan(),
    host.clinicaltags?.items?.length ? host.clinicaltags : GetClinicalTags(controller),
    host.icd10?.items?.length ? host.icd10 : GetICD10(controller),
    host.condition?.items?.length ? host.condition : GetCondition(controller),
    host.academicdx?.items?.length ? host.academicdx : GetAcademicDX(controller),
    GetUserPermission(controller),
    GetOptions(controller),
  ]);

  // ไม่ cache ถ้าโหลดไม่ครบ
  const missing = Object.entries({ organ, clinicaltags, icd10, condition, academicdx })
    .filter(([, value]) => !value)
    .map(([key]) => key);

  if (missing.length > 0) {
    throw new Error(`DEN master load failed: ${missing.join(", ")}`);
  }

  return {
    data: { organ, clinicaltags, icd10, condition, academicdx },
    permission,
    options,
  };
};

/* ------------------------------------------------------ */

/*                           API                          */

/* ------------------------------------------------------ */
const GetOrgan = async () => {
  try {
    const [organ, otherOrgan] = await Promise.all([
      axios.get("/static/organ.json"),
      axios.get("/static/otherOrgan.json"),
    ]);

    return { name: "organ", items: [...organ.data, ...otherOrgan.data] };
  } catch (error) {
    console.error("DEN load organ error: ", error);

    return undefined;
  }
};

const GetClinicalTags = async (controller: any) => {
  const [response, error] = await DentalClinicalFindingList.list({
    apiToken: controller.apiToken,
    params: { limit: 99999, offset: 0 },
  });

  if (error) {
    console.log("DEN GetClinicalTags error: ", error);

    return undefined;
  }

  return { name: "clinicaltags", items: response?.items || [] };
};

const GetICD10 = async (controller: any) => {
  const [response, error] = await DentalIcd10List.list({
    apiToken: controller.apiToken,
    params: { limit: 99999, offset: 0 },
  });

  if (error) {
    console.log("DEN GetICD10 error: ", error);

    return undefined;
  }

  return { name: "ICD-10", items: response?.items || [] };
};

const GetCondition = async (controller: any) => {
  const [response, error] = await MedicalDescriptionList.get({
    apiToken: controller.apiToken,
  });

  if (error) {
    console.log("DEN GetCondition error: ", error);

    return undefined;
  }

  const items = (response?.items || []).map((item: any) => ({ ...item, term: item.medical_description }));

  return { name: "Condition", items };
};

const GetAcademicDX = async (controller: any) => {
  const [response, error] = await ClinicalTermList.list({
    apiToken: controller.apiToken,
    params: { type: "principal_acad_diag" },
  });

  if (error) {
    console.log("DEN GetAcademicDX error: ", error);

    return undefined;
  }

  const items = (response?.items || []).map((item: any) => ({ ...item, term: item.name }));

  return { name: "Academic Dx", items };
};

const GetUserPermission = async (controller: any): Promise<DENUserPermission> => {
  // role group code ของเอกสารปกปิดต่างกันตาม tenant จึงขอทั้งสองแบบ
  const [response, error] = await PermissionM.post({
    apiToken: controller.apiToken,
    data: {
      rolegroup_CAN_VIEW_SECRET_DOCUMENT: false,
      rolegroup_CAN_VIEW_SECRET_DOCUMENT_CODE: false,
      role_PEDIATRIC_DENTISTS: false,
    },
    params: {},
  });

  if (error) {
    console.log("DEN GetUserPermission error: ", error);
  }

  return {
    rolegroup_CAN_VIEW_SECRET_DOCUMENT:
      !!response?.rolegroup_CAN_VIEW_SECRET_DOCUMENT || !!response?.rolegroup_CAN_VIEW_SECRET_DOCUMENT_CODE,
    role_PEDIATRIC_DENTISTS: !!response?.role_PEDIATRIC_DENTISTS,
  };
};

const GetOptions = async (controller: any): Promise<DENMaster["options"]> => {
  const [[urgency], [status], [termset], [treatment], [priceChangeReason], divisionHasUserOptions] = await Promise.all([
    ChoiceView.get({
      apiToken: controller.apiToken,
      params: { model: "DEN.DentalTreatmentPlan", field: "urgency", name_as_id: true },
    }),
    ChoiceView.get({
      apiToken: controller.apiToken,
      params: { model: "DEN.DentalTreatmentPlan", field: "status", name_as_id: true },
    }),
    ClinicalTermSetView.post({
      apiToken: controller.apiToken,
      params: {},
      data: { question: ["DEN_ANESTHETIC"] },
    }),
    TreatmentList.list({
      apiToken: controller.apiToken,
      params: { limit: 5000 },
    }),
    ClinicalTermList.list({
      apiToken: controller.apiToken,
      params: { type: "price_change_reason" },
    }),
    GetDivisionHasUserOptions(controller),
  ]);

  // Emergency, Urgent ขึ้นก่อน
  let sortUrgencyLevel: any[] = [];

  if (urgency?.items?.length > 0) {
    sortUrgencyLevel = [{}, {}];

    urgency.items.forEach((item: any) => {
      if (item?.value === "Emergency") {
        sortUrgencyLevel.splice(0, 1, item);
      } else if (item?.value === "Urgent") {
        sortUrgencyLevel.splice(1, 1, item);
      } else {
        sortUrgencyLevel.push(item);
      }
    });
  }

  return {
    urgencyOptions: CreateOptions(sortUrgencyLevel),
    statusExcludeCompleteOptions: CreateOptions(status?.items || [], false, ["C"]),
    statusIncludeCompleteOptions: CreateOptions(status?.items || [], false),
    anestheticOptions: CreateOptions(termset?.DEN_ANESTHETIC?.items || []),
    treatmentOptions: CreateOptions(treatment?.items || [], false, [], true),
    priceChangeReasonOptions: CreateOptions(priceChangeReason?.items || [], true),
    divisionHasUserOptions,
  };
};

const GetDivisionHasUserOptions = async (controller: any) => {
  const [response, error] = await DivisionHasUserList.list({ apiToken: controller.apiToken, params: {} });

  if (error) {
    console.log("DEN GetDivisionHasUserOptions error: ", error);

    return [];
  }

  const divisionItems: Record<string, any>[] = [];

  for (const item of response?.items || []) {
    if (!divisionItems.some((div) => div.id === item.division.id)) {
      let extra: any = {};

      try {
        extra = JSON.parse(item.extra || "{}");
      } catch (error) {
        console.log("DEN DivisionHasUser extra parse error: ", error);
      }

      divisionItems.push({
        ...item.division,
        useRights: extra.useRights || [],
        isActive: extra.is_Active || false,
      });
    }
  }

  const lang = controller.cookies?.get?.("user_lang");

  return divisionItems
    .filter((item) => item.isActive)
    .map((item) => ({
      key: item.id,
      text: lang === "th" ? item.name : item.name_en,
      useRights: item.useRights,
      value: item.id,
    }));
};

export const CreateOptions = (items: any[], nameAsId = false, exclude: any[] = [], useNameCodeFirst = false): DENOption[] => {
  const mapOption: DENOption[] = [];

  items.forEach((item, index) => {
    if (exclude.indexOf(item.id) === -1) {
      mapOption.push({
        key: index,
        text: useNameCodeFirst
          ? `${item.name_code || item.name || item.name_th || item.name_en || item.value}`
          : `${item.name || item.name_th || item.name_en || item.value || item.name_code}`,
        value: !nameAsId ? item.id : item.name || item.name_th || item.name_en,
        text_en: item.name_en,
      });
    }
  });

  return mapOption;
};
